import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, Calculator, ChevronLeft, Loader2 } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const InsuranceAnalyticsPage = () => {
  const [form, setForm] = useState({
    carrier_name: "",
    policy_type: "auto",
    policy_limits: 100000,
    medical_specials: 0,
    lost_wages: 0,
    future_medicals: 0,
    property_damage: 0,
    injury_severity: "moderate",
    liability_strength: "strong"
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/services/insurance/claims/analyze`, {
        case_id: `case_${Date.now()}`,
        ...form
      });
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="insurance-analytics-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-6xl">
        <div className="mb-6">
          <Link to="/tools/services-hub" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Services Hub
          </Link>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Insurance Analytics & Payout Calculator</h1>
          <p className="text-muted-foreground">Carrier analytics, case valuation, and payout formulas</p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Payout Calculator</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Carrier Name</Label>
                  <Input value={form.carrier_name} onChange={e => setForm({...form, carrier_name: e.target.value})} placeholder="e.g., State Farm" />
                </div>
                <div>
                  <Label>Policy Limits</Label>
                  <Input type="number" value={form.policy_limits} onChange={e => setForm({...form, policy_limits: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Medical Specials</Label>
                  <Input type="number" value={form.medical_specials} onChange={e => setForm({...form, medical_specials: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>Lost Wages</Label>
                  <Input type="number" value={form.lost_wages} onChange={e => setForm({...form, lost_wages: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Injury Severity</Label>
                  <Select value={form.injury_severity} onValueChange={v => setForm({...form, injury_severity: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">Minor</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="severe">Severe</SelectItem>
                      <SelectItem value="permanent">Permanent</SelectItem>
                      <SelectItem value="catastrophic">Catastrophic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Liability Strength</Label>
                  <Select value={form.liability_strength} onValueChange={v => setForm({...form, liability_strength: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strong">Strong</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="weak">Weak</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={analyze} disabled={loading} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />}
                Calculate Case Value
              </Button>
            </CardContent>
          </Card>
          
          {result && (
            <Card data-testid="insurance-results">
              <CardHeader>
                <CardTitle>Case Valuation Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 rounded-lg bg-slate-100 text-center">
                      <p className="text-xs text-muted-foreground">LOW</p>
                      <p className="text-lg font-bold">${result.case_value?.low?.toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-100 text-center">
                      <p className="text-xs text-muted-foreground">MID</p>
                      <p className="text-lg font-bold text-emerald-700">${result.case_value?.mid?.toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-100 text-center">
                      <p className="text-xs text-muted-foreground">HIGH</p>
                      <p className="text-lg font-bold">${result.case_value?.high?.toLocaleString()}</p>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium mb-1">Recommended Demand</p>
                    <p className="text-2xl font-bold text-blue-600">${result.recommended_demand?.toLocaleString()}</p>
                  </div>
                  {result.policy_limited && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>Case value exceeds policy limits. Consider pursuing excess coverage or umbrella policy.</AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default InsuranceAnalyticsPage;
